import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import moment from 'moment';
import ReactTable from 'react-table';

import {
  DATE_FORMAT_FOR_SERVER,
  DATE_FORMAT_FOR_UI,
  ReactTableColumnProperties,
  ReactTableWrapperCogColumnProperties,
} from 'Common/Constants';
import { GridContextMenu } from 'Components/app/contextmenu/ContextMenuManager';
import {
  AppliedMenuFieldEnum as Fields,
  AppliedMenuListFormEnum as FormFields,
} from 'Common/FieldEnum';
import { TitledCard } from 'Components';
import {
  caseInsensitiveFilter,
  PopupTextCell,
  SelectFilter,
  SelectFilterMethod,
  DateColumnFilter,
} from 'Common/GridHelper';
import {
  NutritionalStandard,
  NutritionalStandardMap,
} from 'Common/Constants/NutritionalStandard';
import { NOTIFICATION_DIALOG_MESSAGES } from 'Common/Constants';
import {
  createToast,
  toastType,
  errorToast,
  successToast,
} from 'Common/ToastHelper';
import * as SiteActions from 'State/sites/actions';
import * as SiteSelectors from 'State/sites/selectors';
import { makeOptionsWithNull } from 'Common/FormHelper';
import * as DialogActions from 'Components/dialogs/state/actions';

import * as AppliedMenuSelectors from '../state/listappliedmenus/selectors';
import * as AppliedMenuActions from '../state/listappliedmenus/actions';
import { AppliedMenuListForm } from './AppliedMenuListForm';
import { EditAppliedMenuConfigModal } from '../components/EditAppliedMenuConfigModal';

const NutritionalStandardOptions = makeOptionsWithNull(
  NutritionalStandard.map(standard => ({
    Description: standard.description,
    Id: standard.id,
  }))
);

class AppliedMenuList extends Component {
  state = {
    showConfigModal: false,
    selectedMenu: null,
  };

  startDate = moment()
    .subtract(1, 'month')
    .format(DATE_FORMAT_FOR_UI);
  endDate = moment()
    .add(1, 'month')
    .format(DATE_FORMAT_FOR_UI);

  onDeleteMenuClick = props => {
    const { setNotificationDialog } = this.props;
    setNotificationDialog({
      ...NOTIFICATION_DIALOG_MESSAGES.DELETE_APPLIED_MENU_CONFIRM,
      actionsToDispatchOnOk: [AppliedMenuActions.deleteAppliedMenu(props)],
    });
  };

  get columns() {
    const { sitesMap } = this.props;
    return [
      {
        Header: 'Menu',
        accessor: Fields.Description,
        Cell: row => <PopupTextCell text={row.value} />,
        ...ReactTableColumnProperties,
      },
      {
        id: Fields.SiteId,
        Header: 'Site',
        accessor: row => {
          const site = sitesMap[row[Fields.SiteId]];

          if (site) {
            return site.Description;
          }
          return '';
        },
        Cell: row => <PopupTextCell text={row.value} />,
        ...ReactTableColumnProperties,
      },
      {
        id: Fields.SiteGroup,
        Header: 'Site Group',
        accessor: row => {
          const site = sitesMap[row[Fields.SiteId]];

          if (site) {
            return site.SiteGroupName;
          }
          return '';
        },
        Cell: row => <PopupTextCell text={row.value} />,
        ...ReactTableColumnProperties,
      },
      {
        Header: 'Serving Date',
        accessor: Fields.ServingDT,
        Cell: this.renderDateCell,
        filterMethod: DateColumnFilter,
        ...ReactTableColumnProperties,
      },
      {
        Header: 'Nutritional Standard',
        accessor: Fields.NutritionStdId,
        Cell: row => <PopupTextCell text={NutritionalStandardMap[row.value]} />,
        filterMethod: SelectFilterMethod,
        Filter: props => (
          <SelectFilter {...props} options={NutritionalStandardOptions} />
        ),
        ...ReactTableColumnProperties,
      },
      {
        Cell: gridProps =>
          GridContextMenu({ gridProps, options: this.menuOptions }),
        ...ReactTableWrapperCogColumnProperties,
      },
    ];
  }

  viewAppliedMenu = row => {
    this.toggleConfigModal(true);
    this.setState({
      selectedMenu: row,
    });
  };

  get menuOptions() {
    const { isReadOnly } = this.props;

    return [
      {
        icon: isReadOnly ? 'eye' : 'pencil',
        text: isReadOnly ? 'View' : 'Edit',
        handler: this.viewAppliedMenu,
      },
      // {
      //   icon: 'copy',
      //   text: 'Duplicate',
      //   handler: this.onDuplicateMenuClick,
      //   disabledWhen: x => this.props.isReadOnly,
      // },
      {
        icon: 'trash',
        text: 'Delete',
        className: 'danger',
        handler: this.onDeleteMenuClick,
        disabledWhen: x => this.props.isReadOnly || x.Active === 'N',
      },
    ];
  }

  async componentDidMount() {
    const { getSitesAction } = this.props;
    getSitesAction();
    this.getAppliedMenus();
  }

  componentDidUpdate(prevProps) {
    const { formValues: prevFormValues } = prevProps;
    const { formValues, clearError, setDeleteSuccessful } = this.props;
    const prevStartDate = prevFormValues[FormFields.StartDate];
    const prevEndDate = prevFormValues[FormFields.EndDate];
    const startDate = formValues[FormFields.StartDate];
    const endDate = formValues[FormFields.EndDate];

    errorToast(this.props, prevProps, 'error', clearError);
    successToast(
      this.props,
      prevProps,
      'deleteSuccessful',
      'Applied Menu Deleted',
      () => setDeleteSuccessful(false)
    );
    if (prevStartDate !== startDate || prevEndDate !== endDate) {
      this.getAppliedMenus();
    }
  }

  siteGroupRenderer = row => {
    return row.value;
  };

  getAppliedMenus = () => {
    const { getAppliedMenus, formValues } = this.props;
    const startDate = moment(
      formValues[FormFields.StartDate],
      DATE_FORMAT_FOR_UI,
      true
    );
    const endDate = moment(
      formValues[FormFields.EndDate],
      DATE_FORMAT_FOR_UI,
      true
    );

    if (!startDate.isValid() || !endDate.isValid()) {
      return;
    }

    if (endDate.isBefore(startDate)) {
      createToast(
        toastType.error,
        'Error',
        'End date cannot be before the start date.',
        true
      );
      return;
    }

    getAppliedMenus(
      startDate.format(DATE_FORMAT_FOR_SERVER),
      endDate.format(DATE_FORMAT_FOR_SERVER)
    );
  };

  renderDateCell = row => (
    <PopupTextCell text={moment(row.value).format(DATE_FORMAT_FOR_UI)} />
  );

  toggleConfigModal = show => {
    this.setState({ showConfigModal: show });
  };

  render() {
    const { appliedMenus, fetching, sitesLoading, deleting } = this.props;
    const { showConfigModal, selectedMenu } = this.state;

    return (
      <Fragment>
        <TitledCard
          title="Applied Menus"
          rightItem={
            <AppliedMenuListForm
              initialValues={{
                [FormFields.StartDate]: this.startDate,
                [FormFields.EndDate]: this.endDate,
              }}
            />
          }
          leftColProps={{ width: 4 }}
          rightColProps={{ width: 12 }}
        >
          <ReactTable
            columns={this.columns}
            data={appliedMenus}
            loading={fetching || sitesLoading || deleting}
            minRows={5}
            showPageSizeOptions={false}
            defaultFilterMethod={caseInsensitiveFilter}
            defaultSorted={[
              {
                id: Fields.MenuTitle,
              },
            ]}
          />
          {selectedMenu && (
            <EditAppliedMenuConfigModal
              modalProps={{
                open: showConfigModal,
                onClose: () => this.toggleConfigModal(false),
              }}
              menu={selectedMenu}
            />
          )}
        </TitledCard>
      </Fragment>
    );
  }
}

const mapStateToProps = state => {
  return {
    appliedMenus: AppliedMenuSelectors.getAppliedMenus(state),
    fetching: AppliedMenuSelectors.getFetching(state),
    error: AppliedMenuSelectors.getError(state),
    sitesMap: SiteSelectors.getSitesMap(state),
    sitesLoading: SiteSelectors.getSitesLoading(state),
    formValues: AppliedMenuSelectors.getFormValues(state),
    deleteSuccessful: AppliedMenuSelectors.getDeleteSuccessful(state),
    deleting: AppliedMenuSelectors.getDeleting(state),
  };
};

const mapDispatchToProps = {
  getAppliedMenus: AppliedMenuActions.getAppliedMenus,
  getSitesAction: SiteActions.getSitesAction,
  clearError: AppliedMenuActions.clearError,
  setNotificationDialog: DialogActions.setNotificationDialog,
  setDeleteSuccessful: AppliedMenuActions.setDeleteSuccessful,
};

AppliedMenuList = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(AppliedMenuList)
);

export { AppliedMenuList };



// WEBPACK FOOTER //
// ./src/pages/managemenu/tabs/AppliedMenuList.js